'use client'

/**
 * HtmlBoxIframe - 用于渲染 <html-box> 内容的 iframe 组件
 * 使用 blob URL 方式绕过 CSP 限制
 */

import { memo, useEffect, useMemo, useRef, useState } from 'react'
import useI18n from 'Hooks/useI18n'

import {
  getOrCreateEntry,
  releaseEntry,
  updateHeight,
} from './iframeCacheManager'

interface HtmlBoxIframeProps {
  content: string
}

function buildIframeHtml(content: string, iframeId: string): string {
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8" />
<style>
html {
  color-scheme: normal;
}
html, body {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  background: transparent;
  overflow-y: hidden;
}
</style>
</head>
<body>
${content}
<script>
  var iframeId = '${iframeId}';
  var lastHeight = 0;
  var lastWidth = 0;
  var debounceTimer = null;

  function measureHeight() {
    document.body.style.height = 'auto';
    document.documentElement.style.height = 'auto';
    return Math.ceil(document.body.getBoundingClientRect().height);
  }

  function reportSize() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function() {
      var h = measureHeight();
      var w = document.body.scrollWidth;

      if (Math.abs(h - lastHeight) > 2) {
        lastHeight = h;
        lastWidth = w;
        window.parent.postMessage({
          type: 'iframe-size',
          height: h,
          width: w,
          id: iframeId
        }, '*');
      }
    }, 150);
  }

  setTimeout(reportSize, 50);

  if (window.ResizeObserver) {
    new ResizeObserver(function() {
      requestAnimationFrame(reportSize);
    }).observe(document.body);
  } else {
    new MutationObserver(reportSize).observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  document.querySelectorAll('img').forEach(function(img) {
    img.onload = reportSize;
  });

  window.addEventListener('load', function() {
    setTimeout(reportSize, 100);
  });

  setTimeout(reportSize, 500);
</script>
</body>
</html>`
}

function HtmlBoxIframe({ content }: HtmlBoxIframeProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null)

  const { t } = useI18n()
  const entry = useMemo(
    () => getOrCreateEntry(content, (id) => buildIframeHtml(content, id)),
    [content]
  )

  const [height, setHeight] = useState(entry.height)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    setHeight(entry.height)
  }, [entry.height])

  useEffect(() => {
    setLoaded(false)
  }, [entry.blobUrl])

  // 监听来自 iframe 的消息
  useEffect(() => {
    let localLastHeight = 0

    const handleMessage = (event: MessageEvent) => {
      const iframeWindow = iframeRef.current?.contentWindow
      if (!iframeWindow || event.source !== iframeWindow) {
        return
      }

      if (
        event.data?.type !== 'iframe-size' ||
        event.data?.id !== entry.iframeId
      ) {
        return
      }

      const newHeight = Number(event.data.height)
      if (!Number.isFinite(newHeight) || newHeight < 0) {
        return
      }

      if (Math.abs(newHeight - localLastHeight) > 2) {
        localLastHeight = newHeight
        setHeight(newHeight)
        updateHeight(content, newHeight)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [content, entry.iframeId])

  // content 变化/卸载时释放缓存引用（不销毁 blob URL）
  useEffect(() => {
    return () => {
      releaseEntry(content)
    }
  }, [content])

  return (
    <iframe
      ref={iframeRef}
      title={t('Rich content')}
      src={entry.blobUrl}
      width="100%"
      sandbox="allow-scripts"
      data-rum-ready={loaded || height > 0 ? 'true' : undefined}
      onLoad={() => setLoaded(true)}
      onError={() => setLoaded(true)}
      style={{
        border: 'none',
        overflow: 'hidden',
        colorScheme: 'normal',
        height,
        display: 'block',
      }}
    />
  )
}

export default memo(HtmlBoxIframe)
